'use client'
import { Fragment, type FragmentInstance, type ReactNode, useRef, useState } from 'react'
import styled from 'styled-components'
import { useIntersectionObserver } from '@/features/shared/hooks/useIntersectionObserver'

// For element impressions, an element is considered in viewport when 50% of it scrolls into the screen.
// This heuristic is determined by Shipt's Data Science team.
export const SCROLL_THRESHOLD = 0.5

const useOnceInView = <TElement extends Element | FragmentInstance = HTMLDivElement>({
  threshold = SCROLL_THRESHOLD,
  rootMargin,
  onInView,
  initialInView = false,
}: Pick<Props, 'threshold' | 'rootMargin'> & {
  onInView?: () => void
  initialInView?: boolean
}) => {
  const ref = useRef<TElement>(null)
  // we use a ref, not state since with a Fragment ref, the callback fires once per child element in
  // the same batch, and a state update wouldn't be seen in time to stop repeat onInView calls.
  const hasBeenInView = useRef(initialInView)
  const [inView, setInView] = useState(initialInView)

  const callback = (isIntersecting: boolean) => {
    if (hasBeenInView.current || !isIntersecting) return

    hasBeenInView.current = true
    onInView?.()
    setInView(true)
  }

  useIntersectionObserver(ref, {
    threshold,
    callback,
    rootMargin,
    frozen: inView,
  })

  return { ref, inView }
}

type Props = {
  threshold?: number
  rootMargin?: string
  children: ReactNode
  className?: string
}

export type ScrollIntoViewProps = Props & {
  // If true, the container will use auto height and width
  autoSize?: boolean
  onInView: () => void
  component?: keyof HTMLElementTagNameMap
}

export const ScrollIntoView = ({
  autoSize,
  onInView,
  className,
  threshold,
  children,
  component = 'div',
  rootMargin,
  ...restProps
}: ScrollIntoViewProps) => {
  const { ref } = useOnceInView({
    threshold,
    onInView,
    rootMargin,
  })

  return (
    <ScrollIntoViewContainer
      className={className}
      ref={ref}
      as={component}
      $autoSize={autoSize}
      {...restProps}
    >
      {children}
    </ScrollIntoViewContainer>
  )
}

const ScrollIntoViewContainer = styled.div<{ $autoSize?: boolean }>`
  height: ${({ $autoSize }) => ($autoSize ? 'auto' : '100%')};
  width: ${({ $autoSize }) => ($autoSize ? 'auto' : '100%')};
`

type LazyLoadObserveProps = Props & {
  eagerLoad?: boolean
  minHeight: number
  onInView?: () => void
}

export const LazyLoadObserve = ({
  rootMargin = '100px',
  threshold = 0.01,
  children,
  className,
  eagerLoad = false,
  minHeight,
  onInView,
  ...restProps
}: LazyLoadObserveProps) => {
  const { ref, inView } = useOnceInView({
    rootMargin,
    threshold,
    onInView,
    initialInView: eagerLoad,
  })

  return (
    <LazyLoadObserveContainer ref={ref} className={className} $minHeight={minHeight} {...restProps}>
      {inView ? children : null}
    </LazyLoadObserveContainer>
  )
}

const LazyLoadObserveContainer = styled.div<{ $minHeight: number }>`
  min-height: ${({ $minHeight }) => `${$minHeight}px`};
`

type InViewProps = {
  threshold?: number
  rootMargin?: string
  onInView: () => void
  children: ReactNode
}

/**
 * tracks an impression once without adding a wrapper element.
 * prefer InView for new impression tracking, including product cards and lists.
 * shares observers with ScrollIntoView when threshold and rootMargin match.
 *
 * use InView when the content already renders a measurable DOM element.
 * keep styling and DOM attributes on that child element.
 *
 * keep ScrollIntoView for existing usages that depend on its wrapper's
 * styling, sizing, or attributes. when migrating, move those responsibilities
 * onto a child container and verify that layout and measurement stay correct.
 *
 * use LazyLoadObserve to defer rendering children until its placeholder
 * approaches the viewport. InView always renders its children.
 *
 * avoid overlapping subscriptions to the same DOM element, such as nested
 * InView components that both observe the same child.
 */
export const InView = ({ onInView, threshold, rootMargin, children }: InViewProps) => {
  const { ref } = useOnceInView<FragmentInstance>({
    threshold,
    onInView,
    rootMargin,
  })

  return <Fragment ref={ref}>{children}</Fragment>
}
